// Public-domain classical themes, one per app section. Notes are [name, beats] (quarter note = 1 beat, 'R' = rest).
// `bass` holds one root note per bar, played as half-bar pulses under the melody.

const triplets = (...chords) => chords.flatMap(chord => [...Array(4)].flatMap(() => chord.map(note => [note, 1 / 3])));
const run = (...notes) => notes.map(note => [note, 0.25]);

export const CLASSICAL_SCORES = {
  // Beethoven: "Ode to Joy" (Symphony No. 9)
  multiplayer: {
    title: 'Ode to Joy', composer: 'Beethoven', bpm: 112, beatsPerBar: 4, wave: 'triangle', volume: 0.05,
    melody: [
      ['E4', 1], ['E4', 1], ['F4', 1], ['G4', 1], ['G4', 1], ['F4', 1], ['E4', 1], ['D4', 1],
      ['C4', 1], ['C4', 1], ['D4', 1], ['E4', 1], ['E4', 1.5], ['D4', 0.5], ['D4', 2],
      ['E4', 1], ['E4', 1], ['F4', 1], ['G4', 1], ['G4', 1], ['F4', 1], ['E4', 1], ['D4', 1],
      ['C4', 1], ['C4', 1], ['D4', 1], ['E4', 1], ['D4', 1.5], ['C4', 0.5], ['C4', 2],
      ['D4', 1], ['D4', 1], ['E4', 1], ['C4', 1], ['D4', 1], ['E4', 0.5], ['F4', 0.5], ['E4', 1], ['C4', 1],
      ['D4', 1], ['E4', 0.5], ['F4', 0.5], ['E4', 1], ['D4', 1], ['C4', 1], ['D4', 1], ['G3', 2],
      ['E4', 1], ['E4', 1], ['F4', 1], ['G4', 1], ['G4', 1], ['F4', 1], ['E4', 1], ['D4', 1],
      ['C4', 1], ['C4', 1], ['D4', 1], ['E4', 1], ['D4', 1.5], ['C4', 0.5], ['C4', 2],
    ],
    bass: ['C3', 'G2', 'C3', 'G2', 'C3', 'G2', 'C3', 'C3', 'G2', 'C3', 'G2', 'G2', 'C3', 'G2', 'C3', 'C3'],
  },

  // Mozart: "Eine kleine Nachtmusik", K. 525
  solo: {
    title: 'Eine kleine Nachtmusik', composer: 'Mozart', bpm: 132, beatsPerBar: 4, wave: 'triangle', volume: 0.05,
    melody: [
      ['G4', 1], ['R', 0.5], ['D4', 0.5], ['G4', 1], ['R', 0.5], ['D4', 0.5],
      ['G4', 0.5], ['D4', 0.5], ['G4', 0.5], ['B4', 0.5], ['D5', 2],
      ['C5', 1], ['R', 0.5], ['A4', 0.5], ['C5', 1], ['R', 0.5], ['A4', 0.5],
      ['C5', 0.5], ['A4', 0.5], ['F#4', 0.5], ['A4', 0.5], ['D4', 2],
      ['G4', 1], ['R', 0.5], ['D4', 0.5], ['G4', 1], ['R', 0.5], ['D4', 0.5],
      ['G4', 0.5], ['D4', 0.5], ['G4', 0.5], ['B4', 0.5], ['D5', 2],
      ['C5', 1], ['R', 0.5], ['A4', 0.5], ['C5', 1], ['R', 0.5], ['A4', 0.5],
      ['C5', 0.5], ['A4', 0.5], ['F#4', 0.5], ['A4', 0.5], ['G4', 2],
    ],
    bass: ['G2', 'G2', 'C3', 'D3', 'G2', 'G2', 'C3', 'G2'],
  },

  // Beethoven: "Für Elise"
  familytree: {
    title: 'Für Elise', composer: 'Beethoven', bpm: 84, beatsPerBar: 1.5, wave: 'sine', volume: 0.07,
    melody: [
      ['R', 0.5], ['E5', 0.5], ['D#5', 0.5],
      ['E5', 0.5], ['D#5', 0.5], ['E5', 0.5],
      ['B4', 0.5], ['D5', 0.5], ['C5', 0.5],
      ['A4', 1], ['R', 0.5],
      ['C4', 0.5], ['E4', 0.5], ['A4', 0.5],
      ['B4', 1], ['R', 0.5],
      ['E4', 0.5], ['G#4', 0.5], ['B4', 0.5],
      ['C5', 1], ['R', 0.5],
      ['E4', 0.5], ['E5', 0.5], ['D#5', 0.5],
      ['E5', 0.5], ['D#5', 0.5], ['E5', 0.5],
      ['B4', 0.5], ['D5', 0.5], ['C5', 0.5],
      ['A4', 1], ['R', 0.5],
      ['C4', 0.5], ['E4', 0.5], ['A4', 0.5],
      ['B4', 1], ['R', 0.5],
      ['E4', 0.5], ['C5', 0.5], ['B4', 0.5],
      ['A4', 1.5],
    ],
    bass: ['A2', 'A2', 'A2', 'A2', 'A2', 'E2', 'E2', 'A2', 'A2', 'A2', 'A2', 'A2', 'A2', 'E2', 'E2', 'A2'],
  },

  // Mozart: "Rondo alla Turca" (Piano Sonata No. 11)
  chat: {
    title: 'Rondo alla Turca', composer: 'Mozart', bpm: 120, beatsPerBar: 2, wave: 'triangle', volume: 0.045,
    melody: [
      ...run('B4', 'A4', 'G#4', 'A4'), ['C5', 1],
      ...run('D5', 'C5', 'B4', 'C5'), ['E5', 1],
      ...run('F5', 'E5', 'D#5', 'E5'), ...run('B5', 'A5', 'G#5', 'A5'),
      ...run('B5', 'A5', 'G#5', 'A5'), ['C6', 1],
      ['A5', 0.5], ['C6', 0.5], ['B5', 0.5], ['A5', 0.5],
      ['A5', 2],
    ],
    bass: ['A2', 'A2', 'D3', 'E2', 'A2', 'A2'],
  },

  // Beethoven: "Moonlight Sonata" (Piano Sonata No. 14, first movement)
  calm: {
    title: 'Moonlight Sonata', composer: 'Beethoven', bpm: 56, beatsPerBar: 4, wave: 'sine', volume: 0.06,
    melody: triplets(
      ['G#3', 'C#4', 'E4'], ['G#3', 'C#4', 'E4'], ['A3', 'C#4', 'E4'], ['A3', 'C#4', 'E4'],
      ['A3', 'C#4', 'F#4'], ['G#3', 'C4', 'D#4'], ['G#3', 'C#4', 'E4'], ['G#3', 'C#4', 'E4'],
    ),
    bass: ['C#2', 'B1', 'A1', 'A1', 'F#2', 'G#1', 'C#2', 'C#2'],
  },

  // Beethoven: Symphony No. 5 motif
  rankings: {
    title: 'Symphony No. 5', composer: 'Beethoven', bpm: 108, beatsPerBar: 4, wave: 'sawtooth', volume: 0.026,
    melody: [
      ['R', 0.5], ['G4', 0.5], ['G4', 0.5], ['G4', 0.5], ['D#4', 4],
      ['R', 0.5], ['F4', 0.5], ['F4', 0.5], ['F4', 0.5], ['D4', 4],
      ['R', 0.5], ['C5', 0.5], ['C5', 0.5], ['C5', 0.5], ['G#4', 4],
      ['R', 0.5], ['A#4', 0.5], ['A#4', 0.5], ['A#4', 0.5], ['G4', 4],
    ],
    bass: ['C3', 'C3', 'G2', 'G#2', 'G2', 'G2'],
  },
};
